import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image,
  InputGroup, FormControl
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const OrganizationsManager = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  
  // Form states
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // API configuration
  const API_BASE_URL = BASE_URL;
  
  const [formData, setFormData] = useState({
    organization_name: '',
    short_name: '',
    tagline: '',
    description: '',
    logo_path: '',
    logo_alt_text: 'Organization Logo',
    is_active: 1
  });

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(`${API_BASE_URL}/organizations.php`);
      console.log('Organizations data:', response.data);
      
      if (response.data.success) {
        setOrganizations(response.data.data);
      } else {
        setError('Failed to load organizations: ' + response.data.message);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error connecting to server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target.checked ? 1 : 0) : value
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      setUploadingLogo(true);
      const token = localStorage.getItem('token');
      const formDataObj = new FormData();
      formDataObj.append('logo', file);
      
      const response = await axios.post(
        `${API_BASE_URL}/upload_organization_logo.php`,
        formDataObj,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          logo_path: response.data.logo_path,
          logo_alt_text: file.name
        }));
        
        setLogoPreview(response.data.thumbnail_path || response.data.logo_path);
        setSuccess('Logo uploaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to upload logo: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Error uploading logo: ' + err.message);
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo_path: '',
      logo_alt_text: 'Organization Logo'
    }));
    setLogoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (editingOrg) {
        response = await axios.put(
          `${API_BASE_URL}/organizations.php?id=${editingOrg.id}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/organizations.php`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      
      if (response.data.success) {
        setSuccess(editingOrg ? 'Organization updated successfully!' : 'Organization added successfully!');
        setShowModal(false);
        resetForm();
        setEditingOrg(null);
        fetchOrganizations();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Operation failed: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('Error saving organization: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(
          `${API_BASE_URL}/organizations.php?id=${id}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.data.success) {
          setSuccess('Organization deleted successfully!');
          fetchOrganizations();
          setTimeout(() => setSuccess(''), 3000);
        } else {
          setError('Failed to delete organization: ' + (response.data.message || ''));
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('Error deleting organization: ' + err.message);
      }
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE_URL}/organizations.php?id=${id}&action=toggle_active`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setSuccess('Status updated successfully!');
        fetchOrganizations();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to update status: ' + (response.data.message || ''));
      }
    } catch (err) {
      console.error('Toggle error:', err);
      setError('Error updating status: ' + err.message);
    }
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setFormData({
      organization_name: org.organization_name,
      short_name: org.short_name || '',
      tagline: org.tagline || '',
      description: org.description || '',
      logo_path: org.logo_path || '',
      logo_alt_text: org.logo_alt_text || 'Organization Logo',
      is_active: org.is_active
    });
    
    if (org.logo_path) {
      setLogoPreview(org.logo_path);
    }
    
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      organization_name: '',
      short_name: '',
      tagline: '',
      description: '',
      logo_path: '',
      logo_alt_text: 'Organization Logo',
      is_active: 1
    });
    setLogoPreview(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingOrg(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${UPLOAD_BASE_URL}${imagePath}`;
  };

  const truncateText = (text, length) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  if (loading) {
    return (
      <>
        <AdminNavbar user={user} />
        <Container className="mt-5 text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading organizations...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar user={user} />
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col>
            <h2>Organizations Manager</h2>
            <p className="text-muted">Manage organization details and branding</p>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={() => setShowModal(true)}>
              <i className="fas fa-plus me-2"></i>Add Organization
            </Button>
          </Col>
        </Row>

        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th width="80">Logo</th>
                  <th>Organization Name</th>
                  <th>Short Name</th>
                  <th>Tagline</th>
                  <th width="100">Status</th>
                  <th width="150">Created</th>
                  <th width="150">Updated</th>
                  <th width="120">Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.length > 0 ? (
                  organizations.map((org) => (
                    <tr key={org.id}>
                      <td>
                        {org.logo_path ? (
                          <Image
                            src={getImageUrl(org.logo_path)}
                            alt={org.logo_alt_text || org.organization_name}
                            rounded
                            style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                            className="border bg-light"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = `https://via.placeholder.com/50x50?text=${org.short_name || org.organization_name.charAt(0)}`;
                            }}
                          />
                        ) : (
                          <div className="d-flex align-items-center justify-content-center border rounded bg-light"
                               style={{ width: '50px', height: '50px' }}>
                            <i className="fas fa-building text-muted"></i>
                          </div>
                        )}
                      </td>
                      <td>
                        <strong>{org.organization_name}</strong>
                        {org.description && (
                          <div className="text-muted small mt-1">
                            {truncateText(org.description, 80)}
                          </div>
                        )}
                      </td>
                      <td>
                        {org.short_name ? (
                          <Badge bg="secondary" className="fs-6">{org.short_name}</Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        {org.tagline ? (
                          <small className="fst-italic">{org.tagline}</small>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <Badge bg={org.is_active ? 'success' : 'secondary'}>
                          {org.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(org.created_at)}
                        </small>
                      </td>
                      <td>
                        <small className="text-muted">
                          {formatDate(org.updated_at)}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            variant={org.is_active ? "outline-warning" : "outline-success"}
                            onClick={() => handleToggleActive(org.id)}
                            title={org.is_active ? "Deactivate" : "Activate"}
                          >
                            <i className={`fas fa-power-off ${org.is_active ? 'text-warning' : 'text-success'}`}></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleEdit(org)}
                            title="Edit"
                          >
                            <i className="fas fa-edit"></i>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleDelete(org.id)}
                            title="Delete"
                          >
                            <i className="fas fa-trash"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center py-5">
                      <i className="fas fa-building fa-3x text-muted mb-3"></i>
                      <p className="text-muted">No organizations found</p>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => setShowModal(true)}
                      >
                        <i className="fas fa-plus me-1"></i> Add First Organization
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Add/Edit Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingOrg ? 'Edit Organization' : 'Add New Organization'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Organization Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="organization_name"
                      value={formData.organization_name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Tech Solutions Inc."
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Short Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="short_name"
                      value={formData.short_name}
                      onChange={handleInputChange}
                      placeholder="e.g., TSI"
                      maxLength="20"
                    />
                    <Form.Text className="text-muted">
                      Abbreviation or acronym
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              
              <Form.Group className="mb-3">
                <Form.Label>Tagline</Form.Label>
                <Form.Control
                  type="text"
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleInputChange}
                  placeholder="e.g., Innovating Tomorrow, Today"
                  maxLength="255"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Describe the organization's mission, vision, and activities..."
                />
                <Form.Text className="text-muted">
                  Detailed description of the organization
                </Form.Text>
              </Form.Group>
              
              {/* Logo Upload Section */}
              <Card className="mb-3">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-image me-2"></i>
                    Organization Logo
                  </h6>
                  <small className="text-muted">Upload logo (Max 10MB, JPG, PNG, GIF, WebP, SVG)</small>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      {/* Logo Preview */}
                      <div className="mb-3">
                        <div className="border rounded bg-light d-flex align-items-center justify-content-center"
                             style={{ width: '150px', height: '150px', margin: '0 auto' }}>
                          {logoPreview ? (
                            <Image
                              src={getImageUrl(logoPreview)}
                              alt={formData.logo_alt_text}
                              fluid
                              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://via.placeholder.com/150x150?text=${formData.short_name || 'Logo'}`;
                              }}
                            />
                          ) : (
                            <div className="text-center">
                              <i className="fas fa-building fa-3x text-muted mb-2"></i>
                              <p className="text-muted small mb-0">No logo</p>
                            </div>
                          )}
                        </div>
                        
                        {logoPreview && (
                          <div className="text-center mt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeLogo}
                              className="mt-2"
                            >
                              <i className="fas fa-trash me-1"></i> Remove Logo
                            </Button>
                          </div>
                        )}
                      </div>
                    </Col>
                    
                    <Col md={8}>
                      {/* Logo Upload */}
                      <Form.Group className="mb-3">
                        <Form.Label>Upload Logo</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          disabled={uploadingLogo}
                        />
                        <Form.Text className="text-muted">
                          Recommended: Square image, transparent background preferred
                        </Form.Text>
                      </Form.Group>
                      
                      {uploadingLogo && (
                        <div className="mb-3">
                          <Spinner animation="border" size="sm" className="me-2" />
                          <span>Uploading logo...</span>
                        </div>
                      )}
                      
                      {/* Alt Text */}
                      <Form.Group className="mb-3">
                        <Form.Label>Logo Alt Text</Form.Label>
                        <Form.Control
                          type="text"
                          name="logo_alt_text"
                          value={formData.logo_alt_text}
                          onChange={handleInputChange}
                          placeholder="Description for accessibility"
                          maxLength="255"
                        />
                        <Form.Text className="text-muted">
                          Description for screen readers and SEO
                        </Form.Text>
                      </Form.Group>
                      
                      {/* Logo Path (readonly) */}
                      {formData.logo_path && (
                        <Form.Group className="mb-3">
                          <Form.Label>Logo Path</Form.Label>
                          <InputGroup>
                            <FormControl
                              value={formData.logo_path}
                              readOnly
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() => {
                                navigator.clipboard.writeText(formData.logo_path);
                                setSuccess('Logo path copied to clipboard!');
                                setTimeout(() => setSuccess(''), 2000);
                              }}
                            >
                              <i className="fas fa-copy"></i>
                            </Button>
                          </InputGroup>
                        </Form.Group>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
              
              {/* Status */}
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="is_active"
                  name="is_active"
                  label="Active Organization"
                  checked={!!formData.is_active}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  Inactive organizations won't be displayed publicly
                </Form.Text>
              </Form.Group>
              
              {/* Preview Section */}
              <Card className="border-secondary">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-eye me-2"></i>
                    Preview
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex align-items-center gap-3 mb-3">
                    {logoPreview && (
                      <div className="flex-shrink-0">
                        <Image
                          src={getImageUrl(logoPreview)}
                          alt={formData.logo_alt_text}
                          rounded
                          style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                          className="border bg-light"
                        />
                      </div>
                    )}
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-baseline gap-2">
                        <h4 className="mb-0">{formData.organization_name || 'Organization Name'}</h4>
                        {formData.short_name && (
                          <Badge bg="secondary" className="fs-6">{formData.short_name}</Badge>
                        )}
                        <Badge bg={formData.is_active ? 'success' : 'secondary'} className="ms-auto">
                          {formData.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      
                      {formData.tagline && (
                        <p className="text-muted fst-italic mb-1">
                          "{formData.tagline}"
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {formData.description && (
                    <div className="mt-3">
                      <h6>Description:</h6>
                      <p className="text-muted">{formData.description}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={uploadingLogo}>
                {uploadingLogo ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  editingOrg ? 'Update Organization' : 'Add Organization'
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default OrganizationsManager;